import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LakshLogo, LakshMark } from '@/components/LakshLogo';
import { 
  FileText, 
  Calculator, 
  MessageSquare, 
  Shield, 
  Zap, 
  Users, 
  ArrowRight,
  Check,
  ChevronRight,
  Menu,
  X,
  Sparkles,
  ClipboardCheck
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatIndianCurrency, getErrorMessage } from '@/utils/helpers';
import { FeedbackDialog } from '@/components/FeedbackDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistPhone, setWaitlistPhone] = useState('');
  const [waitlistName, setWaitlistName] = useState('');
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistSuccess, setWaitlistSuccess] = useState(false);
  
  // ITC Calculator state
  const [itcPurchases, setItcPurchases] = useState('');
  const [itcGstRate, setItcGstRate] = useState('18');
  const [itcClaimed, setItcClaimed] = useState('');
  const [itcResult, setItcResult] = useState(null);
  const [itcLoading, setItcLoading] = useState(false);

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    setWaitlistLoading(true);
    try {
      await axios.post(`${API}/waitlist`, {
        email: waitlistEmail,
        phone: waitlistPhone,
        name: waitlistName,
        source: 'landing_page'
      });
      setWaitlistSuccess(true);
      toast.success('Welcome to the Founding 50! We\'ll be in touch soon.');
    } catch (error) {
      const message = getErrorMessage(error, 'Something went wrong');
      toast.error(message);
    } finally {
      setWaitlistLoading(false);
    }
  };

  const calculateITC = async () => {
    if (!itcPurchases) {
      toast.error('Please enter your monthly purchases');
      return;
    }
    setItcLoading(true);
    try {
      const response = await axios.post(`${API}/tools/itc-calculator`, {
        monthly_purchases: parseFloat(itcPurchases),
        gst_rate: parseFloat(itcGstRate),
        current_itc_claimed: parseFloat(itcClaimed) || 0
      });
      setItcResult(response.data);
    } catch (error) {
      toast.error('Error calculating ITC');
    } finally {
      setItcLoading(false);
    }
  };

  const features = [
    {
      icon: FileText,
      title: 'GST-Compliant Invoicing',
      description: 'Create professional invoices in 60 seconds with auto GST calculation and HSN codes. UPI payment links coming soon.'
    },
    {
      icon: MessageSquare,
      title: 'WhatsApp Delivery',
      soon: true,
      description: 'Send invoices directly via WhatsApp with embedded payment links. Get paid faster.'
    },
    {
      icon: Calculator,
      title: 'ITC Reconciliation',
      soon: true,
      description: 'Auto-match your purchases with GSTR-2B. Never miss ITC again.'
    },
    {
      icon: Shield,
      title: 'Compliance Co-Pilot',
      description: 'AI-powered assistant that answers GST, TDS and compliance questions using your own numbers. Available 24/7.'
    },
    {
      icon: Zap,
      title: 'One-Click Migration',
      description: 'Import customers, vendors, invoices and expenses from Tally, Vyapar, Zoho or Excel CSV exports. Go live in days, not months.'
    },
    {
      icon: Users,
      title: 'CA Partner Portal',
      description: 'Invite your CA to see your books and GST deadlines. Collaborate without chaos.'
    }
  ];

  const pricingPlans = [
    {
      name: 'Free',
      price: '₹0',
      description: 'For businesses under ₹20L revenue',
      features: ['GST invoicing', 'Customers & vendors', 'Basic dashboard', 'Email support']
    },
    {
      name: 'Starter',
      price: '₹299',
      period: '/month',
      description: '₹20L - ₹1Cr revenue',
      features: ['Everything in Free', 'Expense & ITC tracking', 'AI compliance co-pilot', 'GST return alerts']
    },
    {
      name: 'Growth',
      price: '₹599',
      period: '/month',
      description: 'Start with a 14-day free trial',
      features: ['Everything in Starter', 'CA partner access', 'Tally / Vyapar / Excel migration', 'ITC reconciliation (coming soon)'],
      popular: true
    },
    {
      name: 'Scale',
      price: '₹1,999',
      period: '/month',
      description: '₹1Cr - ₹5Cr revenue',
      features: ['Everything in Growth', 'Unlimited users', 'Payroll & TDS (coming soon)', 'Priority support']
    }
  ];

  return (
    <div className="min-h-screen bg-[#06100A]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#06100A]/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <LakshLogo height={36} />
            </Link>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-gray-400 hover:text-white transition-colors">Features</a>
              <Link to="/migration-assessment" className="text-gray-400 hover:text-white transition-colors">Migration Assessment</Link>
              <a href="#itc-calculator" className="text-gray-400 hover:text-white transition-colors">ITC Calculator</a>
              <a href="#pricing" className="text-gray-400 hover:text-white transition-colors">Pricing</a>
              <Link to="/login" className="text-gray-400 hover:text-white transition-colors">Login</Link>
              <Link to="/signup">
                <Button className="bg-[#EF9F27] hover:bg-[#EF9F27]/90 text-[#06100A] font-semibold btn-press" data-testid="nav-signup-btn">
                  Start Free
                </Button>
              </Link>
            </div>

            <button 
              className="md:hidden p-2 text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#06100A] border-t border-white/10 py-4 px-4">
            <div className="flex flex-col gap-4">
              <a href="#features" className="text-gray-400 py-2">Features</a>
              <Link to="/migration-assessment" className="text-gray-400 py-2">Migration Assessment</Link>
              <a href="#itc-calculator" className="text-gray-400 py-2">ITC Calculator</a>
              <a href="#pricing" className="text-gray-400 py-2">Pricing</a>
              <Link to="/login" className="text-gray-400 py-2">Login</Link>
              <Link to="/signup">
                <Button className="w-full bg-[#EF9F27] hover:bg-[#EF9F27]/90 text-[#06100A] font-semibold">Start Free</Button>
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-[#06100A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fade-in-up">
              <div className="inline-flex items-center gap-2 bg-[#EF9F27]/10 text-[#EF9F27] px-4 py-2 rounded-full text-sm font-medium border border-[#EF9F27]/30">
                <Sparkles className="w-4 h-4" />
                Founding 50 Program Live
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-['Outfit'] tracking-tight leading-tight">
                India's Financial OS for{' '}
                <span className="text-[#1D9E75]">Startups & SMEs</span>
              </h1>
              
              <p className="text-lg text-gray-400 leading-relaxed max-w-lg">
                GST invoicing, expense & ITC tracking, CA collaboration and an AI compliance co-pilot.
                Built for Indian businesses. 14-day free trial, no card needed.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/signup">
                  <Button size="lg" className="bg-[#EF9F27] hover:bg-[#EF9F27]/90 text-[#06100A] font-semibold btn-press gap-2 w-full sm:w-auto" data-testid="hero-cta-btn">
                    Start Free Trial <ArrowRight className="w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/migration-assessment">
                  <Button size="lg" variant="outline" className="border-[#1D9E75] text-[#1D9E75] hover:bg-[#1D9E75]/10 gap-2 w-full sm:w-auto">
                    <ClipboardCheck className="w-5 h-5" /> Migration Assessment
                  </Button>
                </Link>
              </div>

              <div className="flex items-center gap-6 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-600" />
                  No credit card required
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-600" />
                  Free under ₹20L revenue
                </div>
              </div>
            </div>

            <div className="relative animate-fade-in-up stagger-2">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl border border-white/10">
                <img 
                  src="https://images.unsplash.com/photo-1742119971773-57e0131095b0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxJbmRpYW4lMjBwcm9mZXNzaW9uYWwlMjBhY2NvdW50YW50JTIwd29ya2luZyUyMGxhcHRvcCUyMG1vZGVybiUyMG9mZmljZXxlbnwwfHx8fDE3NzQxNTcxMTV8MA&ixlib=rb-4.1.0&q=85"
                  alt="Professional using Laksh"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#0a1a12] border border-[#1D9E75]/30 rounded-xl shadow-lg p-4 animate-fade-in-up stagger-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#1D9E75]/20 rounded-full flex items-center justify-center">
                    <Check className="w-5 h-5 text-[#1D9E75]" />
                  </div>
                  <div>
                    <div className="font-semibold text-white">₹2.4L ITC Recovered</div>
                    <div className="text-sm text-gray-400">This month</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-[#0a1a12]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white font-['Outfit'] mb-4">
              Everything your business needs
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              From invoicing to compliance, Laksh handles it all so you can focus on growing your business.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-[#06100A] border border-white/10 rounded-lg p-6 transition-all duration-200 hover:border-[#1D9E75]/50">
                <div className="w-12 h-12 bg-[#EF9F27]/10 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-[#EF9F27]" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2 font-['Outfit'] flex items-center gap-2">
                  {feature.title}
                  {feature.soon && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#EF9F27]/15 text-[#EF9F27] font-semibold" data-testid={`feature-soon-${index}`}>Coming soon</span>}
                </h3>
                <p className="text-gray-400">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ITC Calculator Section */}
      <section id="itc-calculator" className="py-20 bg-[#06100A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white font-['Outfit'] mb-4">
                Free ITC Recovery Calculator
              </h2>
              <p className="text-lg text-gray-400 mb-8">
                Find out how much Input Tax Credit you could be missing. Most businesses leave 
                <span className="text-[#EF9F27] font-semibold"> 15-20% ITC</span> on the table.
              </p>

              <div className="bg-[#0a1a12] border border-white/10 rounded-xl p-6 space-y-4" data-testid="itc-calculator">
                <div>
                  <label className="text-sm font-medium text-gray-300 mb-1 block">
                    Monthly Purchases (₹)
                  </label>
                  <Input
                    type="number"
                    placeholder="e.g., 500000"
                    value={itcPurchases}
                    onChange={(e) => setItcPurchases(e.target.value)}
                    className="font-mono bg-[#06100A] border-white/20 text-white"
                    data-testid="itc-purchases-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-300 mb-1 block">
                    Average GST Rate (%)
                  </label>
                  <select
                    value={itcGstRate}
                    onChange={(e) => setItcGstRate(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-white/20 bg-[#06100A] px-3 py-2 text-sm text-white"
                    data-testid="itc-gst-rate"
                  >
                    <option value="5">5%</option>
                    <option value="12">12%</option>
                    <option value="18">18%</option>
                    <option value="28">28%</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-300 mb-1 block">
                    ITC Already Claimed This Year (₹)
                  </label>
                  <Input
                    type="number"
                    placeholder="e.g., 100000"
                    value={itcClaimed}
                    onChange={(e) => setItcClaimed(e.target.value)}
                    className="font-mono bg-[#06100A] border-white/20 text-white"
                    data-testid="itc-claimed-input"
                  />
                </div>
                <Button 
                  onClick={calculateITC}
                  disabled={itcLoading}
                  className="w-full bg-[#EF9F27] hover:bg-[#EF9F27]/90 text-[#06100A] font-semibold btn-press"
                  data-testid="itc-calculate-btn"
                >
                  {itcLoading ? 'Calculating...' : 'Calculate Recovery Potential'}
                </Button>

                {itcResult && (
                  <div className="mt-6 p-4 bg-[#1D9E75]/10 rounded-lg border border-[#1D9E75]/30" data-testid="itc-result">
                    <h4 className="font-semibold text-[#1D9E75] mb-3">Your ITC Analysis</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Annual ITC Potential:</span>
                        <span className="font-mono font-semibold text-white">{itcResult.formatted.annual}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Currently Claimed:</span>
                        <span className="font-mono text-white">{formatIndianCurrency(itcResult.current_claimed)}</span>
                      </div>
                      <div className="flex justify-between text-lg pt-2 border-t border-[#1D9E75]/30">
                        <span className="font-semibold text-[#1D9E75]">Recovery Potential:</span>
                        <span className="font-mono font-bold text-[#1D9E75]">{itcResult.formatted.recovery}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="lg:pl-12">
              <div className="bg-gradient-to-br from-[#1D9E75] to-[#1D9E75]/80 rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold font-['Outfit'] mb-4">
                  Want to recover your ITC automatically?
                </h3>
                <p className="text-white/80 mb-6">
                  Track ITC on every purchase in Laksh today. Automatic GSTR-2B reconciliation is coming soon.
                </p>
                <Link to="/signup">
                  <Button className="bg-white text-[#1D9E75] hover:bg-white/90 btn-press gap-2 font-semibold">
                    Start Free Trial <ChevronRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-[#0a1a12]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white font-['Outfit'] mb-4">
              Simple, transparent pricing
            </h2>
            <p className="text-lg text-gray-400">
              Start free. Upgrade when you're ready.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <div 
                key={index}
                className={`rounded-2xl p-8 ${
                  plan.popular 
                    ? 'bg-[#EF9F27] text-[#06100A] ring-4 ring-[#EF9F27] ring-offset-4 ring-offset-[#0a1a12]' 
                    : 'bg-[#06100A] border border-white/10'
                }`}
              >
                {plan.popular && (
                  <div className="text-sm font-medium text-[#06100A]/70 mb-4">Most Popular</div>
                )}
                <h3 className={`text-2xl font-bold font-['Outfit'] mb-2 ${plan.popular ? 'text-[#06100A]' : 'text-white'}`}>
                  {plan.name}
                </h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold font-mono">{plan.price}</span>
                  {plan.period && <span className={plan.popular ? 'text-[#06100A]/70' : 'text-gray-500'}>{plan.period}</span>}
                </div>
                <p className={`mb-6 ${plan.popular ? 'text-[#06100A]/80' : 'text-gray-400'}`}>
                  {plan.description}
                </p>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <Check className={`w-5 h-5 ${plan.popular ? 'text-[#06100A]/60' : 'text-[#1D9E75]'}`} />
                      <span className={plan.popular ? 'text-[#06100A]/80' : 'text-gray-400'}>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/signup">
                  <Button 
                    className={`w-full btn-press font-semibold ${
                      plan.popular 
                        ? 'bg-[#06100A] text-white hover:bg-[#06100A]/90' 
                        : 'bg-[#EF9F27] text-[#06100A] hover:bg-[#EF9F27]/90'
                    }`}
                  >
                    Start 14-day free trial
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Waitlist Section */}
      <section className="py-20 bg-gradient-to-br from-[#EF9F27] to-[#EF9F27]/80">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#06100A] font-['Outfit'] mb-4">
            Join the Founding 50
          </h2>
          <p className="text-lg text-[#06100A]/80 mb-8">
            Get 50% off for life and direct access to the founding team. 
            Only 50 spots available.
          </p>

          {waitlistSuccess ? (
            <div className="bg-[#06100A]/10 backdrop-blur rounded-xl p-8 text-[#06100A]">
              <div className="w-16 h-16 bg-[#1D9E75] rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-2">You're in!</h3>
              <p className="text-[#06100A]/80">
                Welcome to the Founding 50. We'll reach out within 24 hours.
              </p>
            </div>
          ) : (
            <form onSubmit={handleWaitlistSubmit} className="bg-[#06100A] rounded-xl p-6 max-w-md mx-auto" data-testid="waitlist-form">
              <div className="space-y-4">
                <Input
                  type="text"
                  placeholder="Your name"
                  value={waitlistName}
                  onChange={(e) => setWaitlistName(e.target.value)}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                  required
                  data-testid="waitlist-name"
                />
                <Input
                  type="email"
                  placeholder="Email address"
                  value={waitlistEmail}
                  onChange={(e) => setWaitlistEmail(e.target.value)}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                  required
                  data-testid="waitlist-email"
                />
                <Input
                  type="tel"
                  placeholder="Phone number"
                  value={waitlistPhone}
                  onChange={(e) => setWaitlistPhone(e.target.value)}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                  data-testid="waitlist-phone"
                />
                <Button 
                  type="submit"
                  disabled={waitlistLoading}
                  className="w-full bg-[#EF9F27] hover:bg-[#EF9F27]/90 text-[#06100A] font-semibold btn-press"
                  data-testid="waitlist-submit-btn"
                >
                  {waitlistLoading ? 'Joining...' : 'Join Waitlist'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[#06100A] border-t border-white/10 text-gray-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <LakshLogo height={32} />
            <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm">
              <a href="#features" className="hover:text-white transition-colors">Features</a>
              <Link to="/migration-assessment" className="hover:text-white transition-colors">Assessment</Link>
              <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
              <Link to="/login" className="hover:text-white transition-colors">Login</Link>
              <Link to="/ca/login" className="hover:text-white transition-colors" data-testid="footer-ca-login">For CAs</Link>
              <FeedbackDialog portal="landing" askEmail triggerClassName="hover:text-white" />
            </div>
            <div className="text-sm">
              © 2026 Laksh. Made with love in India.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
